import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { ToWords } from "to-words";

pdfMake.vfs = pdfFonts.vfs;

export const generateContraPdf = (voucher) => {
  const formatAmount = (num) => {
    if (!num && num !== 0) return "0.00";
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const toWords = new ToWords({ localeCode: "en-IN" });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString("en-GB");
  };

  const toLabel = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const totalAmt = voucher?.cash_amount || voucher?.amount || 0;

  // Determine transfer type
  const voucherType = voucher?.voucher_type?.toLowerCase() || "";
  const isBankToBank = voucherType.includes("bank to bank");
  const isCashToBank = voucherType.includes("cash to bank");
  const isBankToCash = voucherType.includes("bank to cash");

  // Function to get transfer details based on type
  const getTransferDetails = () => {
    if (isBankToBank) {
      return {
        title: "BANK TO BANK TRANSFER",
        leftSection: [
          [
            { text: "From Bank", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.from_bank_name || voucher?.from_account_name || "-"
              }`,
              style: "value",
            },
          ],
          [
            { text: "Amount in Words", bold: true, style: "label" },
            {
              text: `: ${toWords.convert(totalAmt || 0)} Only`,
              style: "inWords",
            },
          ],
        ],
        rightSection: [
          [
            { text: "To Bank", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.to_bank_name || voucher?.to_account_name || "-"
              }`,
              style: "value",
            },
          ],
        ],
      };
    } else if (isCashToBank) {
      return {
        title: "CASH TO BANK DEPOSIT",
        leftSection: [
          [
            { text: "From Account", bold: true, style: "label" },
            {
              text: `: ${voucher?.from_account_name || "Cash Account"}`,
              style: "value",
            },
          ],
          [
            { text: "Cash Amount", bold: true, style: "label" },
            { text: `: ₹${formatAmount(totalAmt)}`, style: "value" },
          ],
          [
            { text: "Deposited By", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.deposited_by || voucher?.created_by_name || "-"
              }`,
              style: "value",
            },
          ],
        ],
        rightSection: [
          [
            { text: "To Bank", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.to_bank_name || voucher?.to_account_name || "-"
              }`,
              style: "value",
            },
            
          ],
          [
            { text: "Deposit Date", bold: true, style: "label" },
            {
              text: `: ${formatDate(voucher?.deposit_date || voucher?.date)}`,
              style: "value",
            },
          ],
          [
            { text: "Amount in Words", bold: true, style: "label" },
            {
              text: `: ${toWords.convert(totalAmt || 0)} Only`,
              style: "inWords",
            },
          ],
        ],
      };
    } else if (isBankToCash) {
      return {
        title: "BANK TO CASH WITHDRAWAL",
        leftSection: [
          [
            { text: "From Bank", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.from_bank_name || voucher?.from_account_name || "-"
              }`,
              style: "value",
            },
          ],
       
          [
            { text: "Cash Amount", bold: true, style: "label" },
            { text: `: ₹${formatAmount(totalAmt)}`, style: "value" },
          ],
          [
            { text: "Withdrawn By", bold: true, style: "label" },
            {
              text: `: ${
                voucher?.withdrawn_by || voucher?.created_by_name || "-"
              }`,
              style: "value",
            },
          ],
        ],
        rightSection: [
          [
            { text: "To Account", bold: true, style: "label" },
            {
              text: `: ${voucher?.to_account_name || "Cash Account"}`,
              style: "value",
            },
          ],
          [
            { text: "Cheque/Ref No.", bold: true, style: "label" },
            {
              text: `: ${voucher?.cheque_no || voucher?.reference_no || "-"}`,
              style: "value",
            },
          ],
          [
            { text: "Amount in Words", bold: true, style: "label" },
            {
              text: `: ${toWords.convert(totalAmt || 0)} Only`,
              style: "inWords",
            },
          ],
        ],
      };
    } else {
      // Default case
      return {
        title: "ACCOUNT TRANSFER DETAILS",
        leftSection: [
          [
            { text: "From Account", bold: true, style: "label" },
            { text: `: ${voucher?.from_account_name || "-"}`, style: "value" },
          ],
        ],
        rightSection: [
          [
            { text: "To Account", bold: true, style: "label" },
            { text: `: ${voucher?.to_account_name || "-"}`, style: "value" },
          ],
        ],
        additionalDetails: [],
      };
    }
  };

  const transferDetails = getTransferDetails();

  const docDefinition = {
    pageMargins: [40, 40, 40, 40],

    content: [
      {
        table: {
          widths: ["100%"],
          body: [
            [
              {
                table: {
                  widths: [80, "*"],
                  body: [
                    [
                      {
                        image: "logoImg",
                        width: 80,
                        margin: [0, 0, 10, 0],
                      },
                      {
                        text: `K P AND GRANDSONS`,
                        alignment: "center",
                        bold: true,
                        fontSize: 25,
                        margin: [0, 10, 0, 0],
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              },
            ],

            [
              {
                text: "CONTRA VOUCHER",
                style: "voucherTitle",
                alignment: "center",
              },
            ],

            [
              {
                columns: [
                  {
                    width: "50%",
                    table: {
                      widths: ["40%", "60%"],
                      body: [
                        [
                          { text: "Voucher No.", bold: true, style: "label" },
                          {
                            text: `: ${voucher?.voucher_no || "_____"}`,
                            style: "value",
                          },
                        ],
                        [
                          { text: "Date", bold: true, style: "label" },
                          {
                            text: `: ${
                              voucher?.date ? formatDate(voucher.date) : "-"
                            }`,
                            style: "value",
                          },
                        ],
                        [
                          { text: "Transfer Type", bold: true, style: "label" },
                          {
                            text: `: ${toLabel(voucher?.voucher_type) || "-"}`,
                            style: "value",
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                  {
                    width: "50%",
                    table: {
                      widths: ["40%", "60%"],
                      body: [
                        [
                          { text: "Status", bold: true, style: "label" },
                          {
                            text: `: ${voucher?.status || "Pending"}`,
                            style: "value",
                          },
                        ],
                        [
                          { text: "Amount", bold: true, style: "label" },
                          {
                            text: `: ₹${formatAmount(totalAmt)}`,
                            style: "value",
                          },
                        ],
                        [
                          { text: "Created By", bold: true, style: "label" },
                          {
                            text: `: ${voucher?.created_by_name || "-"}`,
                            style: "value",
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                ],
              },
            ],

            // Transfer Details Section (Dynamic based on type)
            [
              {
                text: transferDetails.title,
                style: "voucherTitle",
                alignment: "center",
              },
            ],

            [
              {
                columns: [
                  {
                    width: "50%",
                    table: {
                      widths: ["40%", "60%"],
                      body: transferDetails.leftSection,
                    },
                    layout: "noBorders",
                  },
                  {
                    width: "50%",
                    table: {
                      widths: ["40%", "60%"],
                      body: transferDetails.rightSection,
                    },
                    layout: "noBorders",
                  },
                ],
              },
            ],

            // Narration
            [
              {
                columns: [
                  {
                    width: "100%",
                    table: {
                      widths: ["15%", "85%"],
                      body: [
                        [
                          { text: "Narration", bold: true, style: "label" },
                          {
                            text: `: ${voucher.narration || "-"}`,
                            style: "value",
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                ],
              },
            ],

            // Remarks (if any)
            ...(voucher.remarks
              ? [
                  [
                    {
                      columns: [
                        {
                          width: "100%",
                          table: {
                            widths: ["15%", "85%"],
                            body: [
                              [
                                { text: "Remarks", bold: true, style: "label" },
                                {
                                  text: `: ${voucher.remarks || "-"}`,
                                  style: "value",
                                },
                              ],
                            ],
                          },
                          layout: "noBorders",
                        },
                      ],
                    },
                  ],
                ]
              : []),

            // Signatures
            [
              {
                table: {
                  widths: ["25%", "25%", "25%", "25%"],
                  body: [
                    // Space for signatures
                    [
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        margin: [0, 30, 0, 0],
                      },
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        margin: [0, 30, 0, 0],
                      },
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        margin: [0, 30, 0, 0],
                      },
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        margin: [0, 30, 0, 0],
                      },
                    ],
                    // Names
                    [
                      {
                        text: voucher.created_by_name || "",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: "",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: voucher.approved_name || "",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                    ],
                    // Labels
                    [
                      {
                        text: "Prepared By",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: "Checked By",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: "Verified By",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                      {
                        text: "Approved By",
                        alignment: "center",
                        border: [false, false, false, false],
                        style: "tableValue",
                      },
                    ],
                  ],
                },
                layout: "noBorders",
                margin: [0, 20, 0, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
        },
        margin: [0, 10, 0, 20],
      },
    ],

    styles: {
      header: {
        fontSize: 14,
        bold: true,
      },
      subheader: {
        fontSize: 10,
      },
      voucherTitle: {
        fontSize: 10,
        bold: true,
        fillColor: "#f5f5f5",
      },
      label: {
        fontSize: 9,
        bold: true,
      },
      value: {
        fontSize: 9,
        bold: false,
      },
      tableHeader: {
        fontSize: 9,
        bold: true,
        fillColor: "#e6e6e6",
      },
      tableValue: {
        fontSize: 8,
      },
      inWords: {
        fontSize: 9,
        italics: true,
      },
    },

    images: {
      logoImg:
        "data:image/png;base64,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", // <- apna logo base64 paste karo
    },
  };

  // Generate PDF
  pdfMake.createPdf(docDefinition).open();
};
